import { ReactElement, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { isTabletMobile } from '../../../utils';
import Logo from '../../common/logo';
import { icons } from '../../../utils/menu-utils';
import icMinimize from '../../../assets/icons/arrow-right.svg?url';
import icMenu from '../../../assets/icons/menu.svg?url';
import l from '../../../lang';
import './styles.scss';
import AvatarOptions from '../../common/avatar-options';
import { MenuCodes } from '../../../constants';
import { Routes } from '../../../config/routes';
import { segmentSideBarOption } from '../../../utils/analytics';
import { useDashboard } from '../../../store/dashboard';

interface SubItemMenu {
  [key: string]: string;
}

interface ItemMenu {
  [key: string]: string | SubItemMenu;
}

interface MenuProps {
  items: ItemMenu[];
  selected: string;
  body: ReactElement;
  canOperate: boolean;
}

/** Horizontal rule below these rows only (no rules between Movimientos–Marketing; one line after Marketing). */
const MENU_DIVIDER_BELOW_KEYS = new Set<string>([
  MenuCodes.ACTIVATE_ACCOUNT,
  MenuCodes.SIDEBAR_PAGAR,
  MenuCodes.SIDEBAR_MARKETING,
]);

const showMenuDividerBelow = (key: string, idxGroup: number, idx: number, group: ItemMenu) => {
  if (MENU_DIVIDER_BELOW_KEYS.has(key)) return true;
  const keys = Object.keys(group);
  if (
    idxGroup === 0 &&
    idx === keys.length - 1 &&
    key === MenuCodes.TRANSACTIONS_AFFILIATIONS
  ) {
    return true;
  }
  return false;
};

const FOOTER_MENU_START_KEY = MenuCodes.SIDEBAR_CONFIGURATION;

const Menu = ({ items, selected, body, canOperate }: MenuProps) => {
  const { merchantMe } = useDashboard();
  const { validKYC = false } = merchantMe || {};

  const history = useHistory();
  const location = useLocation();
  const isProfilePage = location.pathname === Routes.PROFILE;
  const [minimize, setMnimize] = useState(isTabletMobile());
  const [openParentKeys, setOpenParentKeys] = useState<string[]>([]);
  const [collapsedParentKeys, setCollapsedParentKeys] = useState<string[]>([]);

  const getSubMenuSelected = (keySelected: string, subItem: string | SubItemMenu) => {
    return typeof subItem !== 'string' && Object.keys(subItem).some(s => s === keySelected);
  };

  useEffect(() => {
    const parentsWithSelectedSub: string[] = [];
    items.forEach(group => {
      Object.keys(group).forEach(k => {
        const v = group[k];
        if (typeof v !== 'string' && v && getSubMenuSelected(selected, v)) {
          parentsWithSelectedSub.push(k);
        }
        if (k === MenuCodes.SIDEBAR_MOVIMIENTOS && location.pathname === Routes.MOVEMENTS) {
          parentsWithSelectedSub.push(k);
        }
        if (k === MenuCodes.SIDEBAR_COBRAR && location.pathname === Routes.CHARGES) {
          parentsWithSelectedSub.push(k);
        }
      });
    });
    setCollapsedParentKeys(prev => prev.filter(k => !parentsWithSelectedSub.includes(k)));
    setOpenParentKeys(prev => {
      const next = prev.filter(k =>
        items.some(group => {
          const v = group[k];
          return typeof v !== 'string' && v && getSubMenuSelected(selected, v);
        }),
      );
      if (location.pathname === Routes.MOVEMENTS && !next.includes(MenuCodes.SIDEBAR_MOVIMIENTOS)) {
        return [...next, MenuCodes.SIDEBAR_MOVIMIENTOS];
      }
      if (location.pathname === Routes.CHARGES && !next.includes(MenuCodes.SIDEBAR_COBRAR)) {
        return [...next, MenuCodes.SIDEBAR_COBRAR];
      }
      return next;
    });
  }, [selected, location.pathname]);

  const isNestedItem = (item: string | SubItemMenu): item is SubItemMenu => typeof item !== 'string';

  const isSubMenuOpen = (parentKey: string, item: string | SubItemMenu) => {
    if (!isNestedItem(item)) return false;
    if (minimize) {
      return getSubMenuSelected(selected, item);
    }
    if (collapsedParentKeys.includes(parentKey)) {
      return false;
    }
    return getSubMenuSelected(selected, item) || openParentKeys.includes(parentKey);
  };

  const toggleNestedParent = (parentKey: string, item: SubItemMenu) => {
    if (isSubMenuOpen(parentKey, item)) {
      setCollapsedParentKeys(prev => (prev.includes(parentKey) ? prev : [...prev, parentKey]));
      setOpenParentKeys(prev => prev.filter(k => k !== parentKey));
      return;
    }
    setCollapsedParentKeys(prev => prev.filter(k => k !== parentKey));
    setOpenParentKeys(prev => (prev.includes(parentKey) ? prev : [...prev, parentKey]));
  };

  const onClickItem = (item: string | SubItemMenu, key: string) => {
    let nextScreen = '';

    if (key === MenuCodes.VIRTUAL_POS) {
      const virtualPos = /[/]([^/]+)/.exec(item)[1];
      const payload = { userId: merchantMe?.id };

      segmentSideBarOption(virtualPos, payload);
      return;
    }

    if (typeof item === 'string') {
      nextScreen = item;
    } else {
      const firstSubItem = Object.values(item)[0];
      if (firstSubItem) {
        nextScreen = firstSubItem;
      }
    }
    if (!nextScreen) return;

    const segmentSideBarName = /[/]([^/]+)/.exec(nextScreen);

    segmentSideBarOption(segmentSideBarName?.length ? segmentSideBarName[1] : nextScreen, null);
    history.push(nextScreen);
  };

  const onClickParentRow = (item: string | SubItemMenu, key: string) => {
    if (key === MenuCodes.VIRTUAL_POS) {
      onClickItem(item, key);
      return;
    }

    if (key === MenuCodes.SIDEBAR_MOVIMIENTOS && isNestedItem(item)) {
      if (minimize) {
        segmentSideBarOption('movements', null);
        history.push(Routes.MOVEMENTS);
        return;
      }
      segmentSideBarOption('movements', null);
      history.push(Routes.MOVEMENTS);
      toggleNestedParent(key, item);
      return;
    }

    if (key === MenuCodes.SIDEBAR_COBRAR && isNestedItem(item)) {
      if (minimize) {
        segmentSideBarOption('charges', null);
        history.push(Routes.CHARGES);
        return;
      }
      segmentSideBarOption('charges', null);
      history.push(Routes.CHARGES);
      toggleNestedParent(key, item);
      return;
    }

    if (isNestedItem(item)) {
      if (minimize) {
        onClickItem(item, key);
        return;
      }
      toggleNestedParent(key, item);
      return;
    }

    onClickItem(item, key);
  };

  const footerStartIndex = items.findIndex(group => FOOTER_MENU_START_KEY in group);
  const mainMenuGroups = footerStartIndex === -1 ? items : items.slice(0, footerStartIndex);
  const footerMenuGroups = footerStartIndex === -1 ? [] : items.slice(footerStartIndex);

  const renderMenuGroups = (groups: ItemMenu[], groupOffset: number) =>
    groups.map((group, idxGroup) =>
      Object.keys(group).map((key, idx) => (
        <li
          key={`menu-${groupOffset + idxGroup}-${idx}`}
          className={`menu__item ${
            !canOperate && !validKYC && key !== MenuCodes.ACTIVATE_ACCOUNT && key !== MenuCodes.SIDEBAR_RENTABILIDAD
              ? 'menu__item--disabled'
              : ''
          }`}
        >
          <div
            className={`menu__item__container ${key === selected ? 'menu__item__container--selected' : ''} ${
              getSubMenuSelected(selected, group[key]) ? 'menu__item__container--has-subitem-selected' : ''
            }`}
            onClick={() => onClickParentRow(group[key], key)}
          >
            <div className="img-item-menu">{icons(key)}</div>
            {!minimize && (
              <>
                <span className="menu__item__label">{l(`menu.item.${key}`)}</span>
                {isNestedItem(group[key]) && (
                  <KeyboardArrowDown
                    className={`menu__item__chevron ${
                      isSubMenuOpen(key, group[key]) ? 'menu__item__chevron--open' : ''
                    }`}
                  />
                )}
              </>
            )}
          </div>
          {isNestedItem(group[key]) && isSubMenuOpen(key, group[key]) && (
            <div className="sub-menu-container">
              {Object.keys(group[key]).map((subKey, subIdx) => {
                const subIcon = icons(subKey);
                return (
                  <div
                    className={`sub-menu-item ${subKey === selected ? 'selected' : ''} ${
                      minimize ? 'minimize' : ''
                    }`}
                    key={`menu-${groupOffset + idxGroup}-${idx}-${subIdx.toString()}`}
                    // @ts-ignore
                    onClick={e => {
                      e.stopPropagation();
                      history.push(group[key][subKey]);
                    }}
                  >
                    {subIcon && <div className="img-sub-item-menu">{subIcon}</div>}
                    {!minimize && <span className="sub-menu-item__label"> {l(`menu.subItem.${subKey}`)}</span>}
                  </div>
                );
              })}
            </div>
          )}
          {showMenuDividerBelow(key, groupOffset + idxGroup, idx, group) && (
            <div className="menu__item__divider" aria-hidden />
          )}
        </li>
      )),
    );

  return (
    <>
      <div className={`arrow_minimize ${minimize ? 'minimize' : ''}`} onClick={() => setMnimize(!minimize)}>
        <img className="img-minimize" src={icMinimize} alt="img-minimize" />
      </div>
      <div
        className={`menu-component${isProfilePage ? ' menu-component--profile-page' : ''}`}
        onClick={() => !minimize && isTabletMobile() && setMnimize(true)}
      >
        <nav className={`nav-dashboard ${minimize ? 'nav-dashboard--minimize' : ''}`}>
          <div className="nav-dashboard__bg">
            <div className="nav-dashboard__logo">
              <Logo
                goHomeDisabled={!canOperate}
                size={`${minimize ? 'sm' : 'lg'}`}
                type={!minimize ? 'vertical' : undefined}
              />
            </div>
            <div className="menu-layout">
              <ul className="menu menu__main">{renderMenuGroups(mainMenuGroups, 0)}</ul>
              {footerMenuGroups.length > 0 && (
                <ul className="menu menu__footer">{renderMenuGroups(footerMenuGroups, mainMenuGroups.length)}</ul>
              )}
            </div>
          </div>
        </nav>
        {isTabletMobile() && (
          <section className={`section-top-dashboard ${minimize ? 'section-top-dashboard--minimize' : ''}`}>
            <div className="section-top-dashboard__img-menu" onClick={() => setMnimize(false)}>
              <img src={icMenu} alt="img-menu" />
            </div>
            <div className="section-top-dashboard__logo">
              <Logo type="horizontal" />
            </div>
            <div className="section-top-dashboard__avatar">
              <AvatarOptions />
            </div>
          </section>
        )}
        <section className={`section-dashboard ${minimize ? 'section-dashboard--minimize' : ''}`}>
          <div className="section-dashboard__container">{body}</div>
        </section>
      </div>
    </>
  );
};

export default Menu;
